import { type JSX, onCleanup, onMount } from 'solid-js';
import { fitCanvas, type Paint2D } from '../lib/fleet-live-canvas';
import type { LiveFrame, LiveLoop } from '../lib/fleet-live-loop';
import type { LivePalette } from '../lib/fleet-live-palette';

// One Fleet Live canvas (#6778): registers its painter on the section's ONE frame loop
// (`createLiveLoop`), sizes itself to `width()` x `height(width)` at the capped device
// ratio, and paints nothing where the browser gives no 2D context (the test DOM), nor while
// the section is off screen unless it is `always` (the floating Core's, #6779). The
// canvas is `role="img"` with its label; the text alternative beside it carries the items
// and lanes it draws (`FleetLiveText`).

export type FleetLiveCanvasProps = {
  readonly label: string;
  readonly loop: LiveLoop;
  readonly width: () => number;
  readonly height: (width: number) => number;
  readonly paint: (ctx: Paint2D, p: LivePalette, width: number, f: LiveFrame) => void;
  readonly onPointerMove?: (x: number, y: number, e: PointerEvent) => void;
  readonly onPointerLeave?: () => void;
  readonly onClick?: (x: number, y: number) => void;
  /** Paint while the section is off screen too (the floating Core's canvas, #6779). */
  readonly always?: boolean;
  readonly class?: string;
  readonly canvasRef?: (el: HTMLCanvasElement) => void;
};

export function FleetLiveCanvas(props: FleetLiveCanvasProps): JSX.Element {
  let canvas: HTMLCanvasElement | undefined;
  onMount(() => {
    const release = props.loop.register((f) => {
      if (!f.onScreen && props.always !== true) return;
      const w = Math.floor(props.width());
      const p = props.loop.palette();
      if (canvas === undefined || w <= 0 || p === null) return;
      const ctx = fitCanvas(canvas, w, props.height(w));
      if (ctx !== null) props.paint(ctx, p, w, f);
    });
    onCleanup(release);
  });
  const move = (e: PointerEvent): void => {
    if (canvas === undefined || props.onPointerMove === undefined) return;
    const r = canvas.getBoundingClientRect();
    props.onPointerMove(e.clientX - r.left, e.clientY - r.top, e);
  };
  const click = (e: MouseEvent): void => {
    if (canvas === undefined || props.onClick === undefined) return;
    const r = canvas.getBoundingClientRect();
    props.onClick(e.clientX - r.left, e.clientY - r.top);
  };
  return (
    <canvas
      ref={(el) => {
        canvas = el;
        props.canvasRef?.(el);
      }}
      class={props.class ?? 'fleet-live__canvas'}
      role="img"
      aria-label={props.label}
      onPointerMove={move}
      onPointerLeave={() => props.onPointerLeave?.()}
      onClick={click}
    />
  );
}
